import type { RefObjectForHighchartsReact } from "components/charts/modern/abstract/HighchartsChart";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import handleClickInLegend from "components/charts/modern/helpers/handleClickInLegend";
import PercentageSeriesTooltip from "components/charts/modern/tooltip/PercentageSeriesTooltip";
import useBrazilianRealEstateFundIssuer from "components/company/v3/context/hooks/useBrazilianRealEstateFundIssuer";
import SadFaceIcon from "components/icons/SadFaceIcon";
import getContourColors from "components/stocks/modern/OperatingSegments/getContourColors";
import classNames from "components/ui/classNames";
import { useMinWidthTailwindBreakpoints } from "components/ui/hooks/useTailwindBreakpoints";
import aeonik from "components/utils/aeonik";
import { useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import tinycolor from "tinycolor2";

export default function FiisDistributionOfAssetsContent() {
  const intl = useIntl();

  const chartRef = useRef<RefObjectForHighchartsReact | null>(null);

  const { distributionOfAssets } = useBrazilianRealEstateFundIssuer();
  const breakpoints = useMinWidthTailwindBreakpoints();

  if (!distributionOfAssets) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  const assets = [
    {
      label: intl.formatMessage({ defaultMessage: "Caixa", id: "aXWb4c" }),
      value: Math.round(distributionOfAssets.cash * 100),
    },
    {
      label: intl.formatMessage({ defaultMessage: "Imóveis", id: "uX6eHh" }),
      value: Math.round(distributionOfAssets.realEstate * 100),
    },
    {
      label: intl.formatMessage({ defaultMessage: "Fundos", id: "Nk+yFx" }),
      value: Math.round(distributionOfAssets.fundsOfFunds * 100),
    },
    {
      label: intl.formatMessage({ defaultMessage: "Papéis", id: "NliJqs" }),
      value: Math.round(distributionOfAssets.otherInvestments * 100),
    },
  ]
    .filter((node) => node.value > 0)
    .sort((a, b) => b.value - a.value);

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      type: "pie",
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 0,
      plotBorderColor: colors.slate[100],
    },
    scrollbar: {
      enabled: false,
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    legend: {
      align: "left",
      enabled: true,
      padding: 4,
      alignColumns: false,
      itemDistance: 16,
      layout: "horizontal",
      itemStyle: {
        color: colors.slate[800],
        fontFamily: aeonik.style.fontFamily,
        fontWeight: "500",
        fontSize: "14px",
        textOverflow: "ellipsis",
        width: 240,
        textOutline: "none",
      },
    },
    series: [
      {
        type: "pie",
        size: "70%",
        innerSize: "50%",
        borderRadius: 4,
        showInLegend: true,
        data: assets.map((node, index) => {
          const theme = getContourColors(index);

          const color = theme.background;
          const borderColor = theme.text;

          return {
            name: node.label,
            y: node.value,
            color,
            borderColor,
            borderWidth: 1.5,
            custom: node,
            dataLabels: {
              enabled: breakpoints.lg || breakpoints.xl,
              style: {
                color: colors.slate[800],
                fontFamily: aeonik.style.fontFamily,
                fontWeight: "500",
                fontSize: "13px",
                textOutline: "none",
                textOverflow: "ellipsis",
                width: 240,
              },
            },
          };
        }),
      },
    ],
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      borderColor: colors.slate[200],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        return renderToStaticMarkup(<PercentageSeriesTooltip context={this} />);
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        events: {
          legendItemClick(event) {
            handleClickInLegend(event, this, chartRef);
          },
        },
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            callback: () => {
              return breakpoints.lg || breakpoints.xl;
            },
          },
          chartOptions: {
            legend: {
              enabled: false,
            },
          },
        },
      ],
    },
  };

  return (
    <div className="grid h-auto w-full grid-cols-1 items-start gap-4 lg:grid-cols-[max-content,1fr] lg:gap-8">
      <div className="flex h-full flex-col space-y-4 sm:w-96 sm:max-w-md">
        <div className="flex h-full w-full flex-col space-y-4">
          <div className="h-full rounded-2xl border-none border-slate-200 bg-slate-50/80 p-4">
            <ul className="divide-y divide-gray-100">
              {assets.map(({ label, value }, index) => {
                const theme = getContourColors(index);

                return (
                  <li
                    key={label}
                    className={classNames("flex items-center justify-between gap-x-6 py-4", "first:pt-0 last:pb-0")}
                  >
                    <p className="text-sm font-bold text-gray-700 lg:line-clamp-1">{label}</p>

                    <div className="pl-8">
                      <div className="flex items-center space-x-4">
                        <div>
                          <span className="font-mono text-xs font-semibold text-slate-700">{value}%</span>
                        </div>

                        <span
                          aria-hidden={true}
                          className="relative inline-flex h-2.5 w-2.5 items-center justify-center rounded-full bg-violet-700"
                          style={{ backgroundColor: tinycolor(theme.background).darken(20).toHex8String() }}
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      <div className="flex h-full items-center rounded-xl bg-slate-50/50">
        <HighchartsChart options={options} className="h-full w-full" />
      </div>
    </div>
  );
}
